<template>
  <div class="monitor-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper">
      <i class="el-icon-loading"></i>
      <p>正在加载监控数据...</p>
    </div>
    
    <!-- 错误提示 -->
    <div v-if="errorMsg" class="error-wrapper">
      <el-alert
          :title="'加载失败: ' + errorMsg"
          type="error"
          :closable="false"
          show-icon
      >
        <template slot="default">
          <p>请检查：</p>
          <ul>
            <li>后端服务是否正常运行</li>
            <li>API地址是否正确: https://dy.darknight.games</li>
            <li>浏览器控制台是否有跨域错误</li>
          </ul>
        </template>
      </el-alert>
    </div>

    <!-- 欢迎区域 -->
    <div class="welcome-section" v-if="!loading && !errorMsg">
      <div class="welcome-header">
        <h1><i class="el-icon-s-home"></i> 欢迎回来，{{ username || '用户' }}！</h1>
        <p class="subtitle">系统实时监控面板</p>
      </div>
      <div class="time-info">
        <i class="el-icon-time"></i>
        <span>{{ currentTime }}</span>
      </div>
    </div>
    
    <!-- 数据概览卡片 -->
    <el-row :gutter="20" class="metrics-row" v-if="!loading && !errorMsg">
      <!-- HikariCP 连接池 -->
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="metric-card connection-pool">
          <div class="card-header">
            <i class="el-icon-connection"></i>
            <span>HikariCP 连接池</span>
          </div>
          <div class="card-body">
            <div class="metric-item">
              <span class="label">活跃连接</span>
              <span class="value primary">{{ metrics['hikaricp.connections.active'] || 0 }}</span>
            </div>
            <div class="metric-item">
              <span class="label">空闲连接</span>
              <span class="value success">{{ metrics['hikaricp.connections.idle'] || 0 }}</span>
            </div>
            <div class="metric-item">
              <span class="label">最大连接</span>
              <span class="value info">{{ metrics['hikaricp.connections.max'] || 0 }}</span>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- JVM 内存 -->
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="metric-card memory">
          <div class="card-header">
            <i class="el-icon-cpu"></i>
            <span>JVM 内存</span>
          </div>
          <div class="card-body">
            <div class="metric-item">
              <span class="label">已使用</span>
              <span class="value warning">{{ formatBytes(metrics['jvm.memory.used']) }}</span>
            </div>
            <div class="metric-item">
              <span class="label">最大值</span>
              <span class="value info">{{ formatBytes(metrics['jvm.memory.max']) }}</span>
            </div>
            <div class="metric-item">
              <span class="label">已提交</span>
              <span class="value">{{ formatBytes(metrics['jvm.memory.committed']) }}</span>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- Tomcat 会话 -->
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="metric-card session">
          <div class="card-header">
            <i class="el-icon-user"></i>
            <span>Tomcat 会话</span>
          </div>
          <div class="card-body">
            <div class="metric-item">
              <span class="label">当前活跃</span>
              <span class="value primary">{{ metrics['tomcat.sessions.active.current'] || 0 }}</span>
            </div>
            <div class="metric-item">
              <span class="label">最大活跃</span>
              <span class="value success">{{ metrics['tomcat.sessions.active.max'] || 0 }}</span>
            </div>
            <div class="metric-item">
              <span class="label">创建总数</span>
              <span class="value info">{{ metrics['tomcat.sessions.created'] || 0 }}</span>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 系统 CPU -->
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="metric-card system">
          <div class="card-header">
            <i class="el-icon-monitor"></i>
            <span>系统资源</span>
          </div>
          <div class="card-body">
            <div class="metric-item">
              <span class="label">CPU 使用率</span>
              <span class="value" :class="getCpuClass(metrics['system.cpu.usage'])">
                {{ ((metrics['system.cpu.usage'] || 0) * 100).toFixed(2) }}%
              </span>
            </div>
            <div class="metric-item">
              <span class="label">进程 CPU</span>
              <span class="value" :class="getCpuClass(metrics['process.cpu.usage'])">
                {{ ((metrics['process.cpu.usage'] || 0) * 100).toFixed(2) }}%
              </span>
            </div>
            <div class="metric-item">
              <span class="label">线程数</span>
              <span class="value info">{{ metrics['jvm.threads.live'] || 0 }}</span>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 详细信息区域 -->
    <el-row :gutter="20" class="detail-section" v-if="!loading && !errorMsg">
      <!-- GC 信息 -->
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="detail-card">
          <div slot="header" class="detail-header">
            <i class="el-icon-delete"></i>
            <span>GC 垃圾回收统计</span>
          </div>
          <div class="detail-content">
            <el-table :data="gcData" style="width: 100%" size="small">
              <el-table-column prop="name" label="GC 类型" width="180"></el-table-column>
              <el-table-column prop="count" label="回收次数" align="right"></el-table-column>
              <el-table-column prop="time" label="耗时 (ms)" align="right"></el-table-column>
            </el-table>
          </div>
        </el-card>
      </el-col>

      <!-- 系统信息 -->
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="detail-card">
          <div slot="header" class="detail-header">
            <i class="el-icon-info"></i>
            <span>系统信息</span>
          </div>
          <div class="detail-content">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">操作系统:</span>
                <span class="info-value">{{ systemInfo.os }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Java 版本:</span>
                <span class="info-value">{{ systemInfo.javaVersion }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">JVM 供应商:</span>
                <span class="info-value">{{ systemInfo.jvmVendor }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">运行时间:</span>
                <span class="info-value">{{ systemInfo.uptime }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">启动时间:</span>
                <span class="info-value">{{ systemInfo.startTime }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">处理器核心:</span>
                <span class="info-value">{{ systemInfo.processors }}</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 健康检查状态 -->
    <el-row v-if="!loading && !errorMsg" class="health-section">
      <el-col :span="24">
        <el-card shadow="hover" class="health-card">
          <div slot="header" class="detail-header">
            <i class="el-icon-success"></i>
            <span>服务健康状态</span>
          </div>
          <div class="health-content">
            <div class="health-items">
              <div class="health-item" v-for="item in healthStatus" :key="item.name">
                <div class="health-indicator" :class="item.status"></div>
                <span class="health-name">{{ item.name }}</span>
                <span class="health-desc">{{ item.description }}</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import axios from 'axios';
import * as echarts from 'echarts';

export default {
  name: 'HelloWorld',
  data() {
    return {
      metrics: {}, // 存储指标数据
      loading: false,
      errorMsg: '',
      currentTime: '',
      gcData: [
        { name: 'Young GC', count: 1234, time: 5678 },
        { name: 'Old GC', count: 89, time: 12345 }
      ],
      systemInfo: {
        os: 'Linux 5.4.0',
        javaVersion: 'OpenJDK 11.0.12',
        jvmVendor: 'AdoptOpenJDK',
        uptime: '7天 12小时 30分钟',
        startTime: '2024-01-15 08:30:00',
        processors: 8
      },
      healthStatus: [
        { name: '数据库', status: 'up', description: '连接正常' },
        { name: 'Redis', status: 'up', description: '缓存服务正常' },
        { name: '磁盘空间', status: 'up', description: '剩余 45%' },
        { name: '邮件服务', status: 'up', description: 'SMTP 正常' },
        { name: '外部 API', status: 'warning', description: '响应较慢' }
      ],
      cpuChart: null,
      memoryChart: null,
      chartData: {
        cpu: [],
        memory: []
      }
    };
  }, 
  computed: {
    username() {
      return this.$store.state.username || '未知用户';
    }
  },
  mounted() {
    console.log('监控页面已挂载');
    this.updateTime();
    this.fetchMetrics(); // 初始化获取数据
    // setInterval(this.fetchMetrics, 5000); // 每5秒刷新一次
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },
    getCpuClass(usage) {
      if (!usage) return '';
      const percent = usage * 100;
      if (percent > 80) return 'danger';
      if (percent > 60) return 'warning';
      return 'success';
    },
    goBack() {
      this.$router.go(-1);
    },
    async fetchMetrics() {
      this.loading = true;
      this.errorMsg = '';
      
      try {
        console.log('开始获取监控指标...');
        
        // 定义需要获取的指标列表
        const metricKeys = [
          'hikaricp.connections.active',
          'hikaricp.connections.idle',
          'hikaricp.connections.max',
          'jvm.memory.max',
          'jvm.memory.used',
          'jvm.memory.committed',
          'tomcat.sessions.active.current',
          'tomcat.sessions.active.max',
          'tomcat.sessions.created',
          'system.cpu.usage',
          'process.cpu.usage',
          'jvm.threads.live',
        ];

        // 构造批量请求 URL
        const requests = metricKeys.map(key => {
          console.log(`请求指标: ${key}`);
          return axios.get(`https://dy.darknight.games/actuator/metrics/${key}`, {
            timeout: 10000
          });
        });

        // 并发执行所有请求
        const responses = await Promise.allSettled(requests);

        // 处理响应数据并更新 metrics
        responses.forEach((result, index) => {
          const key = metricKeys[index];
          if (result.status === 'fulfilled') {
            const value = result.value.data?.measurements?.[0]?.value;
            if (value !== undefined) {
              this.$set(this.metrics, key, value);
              console.log(`✓ ${key}: ${value}`);
            }
          } else {
            console.warn(`✗ ${key} 请求失败:`, result.reason?.message);
          }
        });

        console.log('监控数据加载完成:', this.metrics);
      } catch (error) {
        console.error('获取指标失败:', error);
        this.errorMsg = error.message || '网络请求失败';
      } finally {
        this.loading = false;
      }
    },

    // 格式化字节单位
    formatBytes(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.monitor-container {
  padding: 24px;
  min-height: calc(100vh - 60px);
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
}

/* 加载状态 */
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #409EFF;
}

.loading-wrapper i {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-wrapper p {
  font-size: 16px;
  margin: 0;
}

/* 错误提示 */
.error-wrapper {
  margin-bottom: 20px;
}

.error-wrapper ul {
  margin: 10px 0 0 20px;
  padding: 0;
}

/* 欢迎区域 */
.welcome-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.welcome-header h1 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.welcome-header h1 i {
  font-size: 28px;
}

.subtitle {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
}

.time-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  backdrop-filter: blur(10px);
}

.time-info i {
  font-size: 16px;
}

/* 指标行 */
.metrics-row {
  margin-top: 0;
}

/* 指标卡片 */
.metric-card {
  border-radius: 12px;
  border: none;
  transition: all 0.3s ease;
  height: 100%;
  overflow: hidden;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.metric-card ::v-deep .el-card__header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.metric-card ::v-deep .el-card__body {
  padding: 0;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.card-header i {
  font-size: 20px;
}

.connection-pool .card-header i { color: #409EFF; }
.memory .card-header i { color: #67C23A; }
.session .card-header i { color: #E6A23C; }
.system .card-header i { color: #F56C6C; }

.card-body {
  padding: 20px;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #ebeef5;
}

.metric-item:last-child {
  border-bottom: none;
}

.metric-item .label {
  font-size: 14px;
  color: #909399;
}

.metric-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.metric-item .value.primary { color: #409EFF; }
.metric-item .value.success { color: #67C23A; }
.metric-item .value.warning { color: #E6A23C; }
.metric-item .value.info { color: #909399; }
.metric-item .value.danger { color: #F56C6C; }

/* 详细信息区域 */
.detail-section {
  margin-top: 20px;
}

.detail-card {
  border-radius: 12px;
  border: none;
  height: 100%;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail-header i {
  font-size: 18px;
  color: #409EFF;
}

.detail-content {
  padding: 0;
}

/* 信息网格 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.info-item:hover {
  background: #e8ecf1;
  transform: translateX(4px);
}

.info-label {
  font-size: 14px;
  color: #909399;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

/* 健康检查区域 */
.health-section {
  margin-top: 20px;
}

.health-card {
  border-radius: 12px;
  border: none;
}

.health-content {
  padding: 0;
}

.health-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;
}

.health-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: #f5f7fa;
  border-radius: 8px;
  min-width: 200px;
  transition: all 0.3s ease;
}

.health-item:hover {
  background: #e8ecf1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.health-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.health-indicator.up {
  background: #67C23A;
  box-shadow: 0 0 8px rgba(103, 194, 58, 0.5);
}

.health-indicator.down {
  background: #F56C6C;
  box-shadow: 0 0 8px rgba(245, 108, 108, 0.5);
}

.health-indicator.warning {
  background: #E6A23C;
  box-shadow: 0 0 8px rgba(230, 162, 60, 0.5);
}

.health-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  min-width: 80px;
}

.health-desc {
  font-size: 13px;
  color: #909399;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .monitor-container {
    padding: 16px;
  }
  
  .welcome-section {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .welcome-header h1 {
    font-size: 20px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .health-items {
    flex-direction: column;
  }
  
  .health-item {
    width: 100%;
  }
}
</style>
